/**
 * Panel de bienvenida + instalación de la app.
 *
 * @module components/install/InstallGuidePanel
 * @version 6.11.43.00
 *
 * Es la única fuente de verdad del "cómo instalo la app": lo usan tanto el
 * bloqueo del navegador (`BrowserBlockScreen`) como el modal del dashboard
 * (`PWAInstallPrompt`), que hasta ahora tenían dos juegos de instrucciones
 * distintos —y distintos entre sí— para el mismo problema.
 *
 * El orden del contenido es a propósito: primero la buena noticia (ya está
 * adentro de la liga), después el pedido (falta la app), y recién ahí el cómo.
 * Antes arrancaba con "Instalación Requerida", que a un jugador que acababa de
 * anotarse le sonaba a trámite.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Download, Loader2, PartyPopper, ChevronDown } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { getInstallGuideForCurrentBrowser } from '../../utils/installSteps';
import { copyToClipboard } from '../../utils/clipboard';
import { InstallStepsCard } from './InstallStepsCard';

interface InstallGuidePanelProps {
  /** Nombre del jugador, para saludarlo por su nombre. */
  userName?: string | null;
  /**
   * - `welcome`: recién se anotó / entra por primera vez desde el navegador.
   * - `returning`: ya participa de una edición activa; lo más probable es que
   *   ya tenga la app y solo haya que mandarlo ahí.
   */
  variant?: 'welcome' | 'returning';
  /** Se dispara cuando el navegador confirma la instalación nativa. */
  onInstalled?: () => void;
}

/** Beneficios, en una línea cada uno. El detalle largo distraía del paso. */
const PERKS = [
  { emoji: '🔔', text: 'Te avisamos de partidos, pagos y novedades' },
  { emoji: '⚡', text: 'Entrás de un toque, sin buscar el link' },
  { emoji: '📱', text: 'Pantalla completa, sin barras del navegador' },
];

export const InstallGuidePanel: React.FC<InstallGuidePanelProps> = ({
  userName,
  variant = 'welcome',
  onInstalled,
}) => {
  const { nativeInstallAvailable, promptInstall } = usePWAInstall();
  const guide = getInstallGuideForCurrentBrowser();

  const [isPrompting, setIsPrompting] = useState(false);
  const [promptFailed, setPromptFailed] = useState(false);
  // Solo aplica cuando hay botón nativo: sin botón, los pasos son el camino
  // principal y se muestran siempre.
  const [stepsOpen, setStepsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const firstName = userName?.trim().split(' ')[0];

  /**
   * Instalación en un toque. Si el navegador no muestra nada (pasa: el evento
   * se consumió, la ventana perdió el gesto del usuario), no se deja al jugador
   * mirando un botón muerto: se abren los pasos manuales.
   */
  const handleNativeInstall = async () => {
    setIsPrompting(true);
    const accepted = await promptInstall();
    setIsPrompting(false);

    if (accepted) {
      onInstalled?.();
      return;
    }
    setPromptFailed(true);
    setStepsOpen(true);
  };

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(window.location.href);
    if (ok) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }
  };

  const showNativeButton = nativeInstallAvailable && !promptFailed;
  // Con botón nativo los pasos quedan detrás de un "hacelo a mano"; sin botón
  // son el camino principal.
  const stepsArePrimary = !showNativeButton;

  return (
    <div>
      {/* ---------------------------------------------------------------- */}
      {/* Festejo: primero la buena noticia                                 */}
      {/* ---------------------------------------------------------------- */}
      <div className="text-center mb-5">
        <motion.div
          initial={{ scale: 0, rotate: -12 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 12 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-secondary border-2 border-secondary-dark/20 shadow-lg shadow-secondary/40 mb-4"
        >
          <PartyPopper size={40} className="text-primary-dark" />
        </motion.div>

        <h2 className="text-3xl font-display font-black uppercase tracking-tight leading-none text-text-main">
          ¡Ya estás dentro
          <br />
          de La Liga!
        </h2>

        <p className="text-text-muted font-medium mt-3 text-[15px] leading-snug">
          {firstName ? (
            <>
              <span className="text-primary font-bold">{firstName}</span>, tu lugar está
              confirmado. Nos vemos en la cancha 🎾
            </>
          ) : (
            <>Tu lugar está confirmado. Nos vemos en la cancha 🎾</>
          )}
        </p>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* El pedido, en positivo                                            */}
      {/* ---------------------------------------------------------------- */}
      <div className="rounded-2xl bg-primary/[0.06] border border-primary/15 p-4 mb-5 text-center">
        <p className="font-display font-black uppercase tracking-tight text-primary-dark text-lg leading-tight">
          Solo te queda un paso
        </p>
        <p className="text-[15px] text-text-main mt-1 leading-snug">
          {variant === 'returning' ? (
            <>
              Tener nuestra <strong>app</strong> en el celular. Si ya la instalaste,
              abrila desde tu pantalla de inicio; si no, te explicamos cómo.
            </>
          ) : (
            <>
              Tener nuestra <strong>app</strong> en el celular. Te explicamos cómo hacerlo,
              paso a paso.
            </>
          )}
        </p>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Instalación: un toque donde se puede, pasos donde no              */}
      {/* ---------------------------------------------------------------- */}
      {showNativeButton && (
        <button
          onClick={handleNativeInstall}
          disabled={isPrompting}
          className="w-full bg-primary-dark text-white font-display font-black uppercase tracking-widest text-sm py-4 rounded-xl shadow-lg hover:bg-primary transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-70 mb-3"
        >
          {isPrompting ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
          {isPrompting ? 'Instalando…' : 'Instalar la app'}
        </button>
      )}

      {promptFailed && (
        <p className="text-xs text-text-muted text-center mb-3">
          ¿No pasó nada al tocar el botón? Tranqui, se hace a mano en {guide.steps.length} toques 👇
        </p>
      )}

      {/* Copiar enlace: en iPhone con Chrome/Edge hay que mudarse a Safari */}
      {guide.platform === 'ios-other' && (
        <button
          onClick={handleCopyLink}
          className="w-full border-2 border-primary/20 text-primary-dark font-bold py-3 rounded-xl hover:bg-primary/5 transition-all flex items-center justify-center gap-2 active:scale-[0.98] mb-3 text-sm"
        >
          {linkCopied ? <Check size={18} className="text-success" /> : <Copy size={18} />}
          {linkCopied ? '¡Copiado! Abrí Safari y pegalo' : 'Copiar el enlace'}
        </button>
      )}

      {stepsArePrimary ? (
        <InstallStepsCard guide={guide} className="mb-5" />
      ) : (
        <div className="mb-5">
          <button
            onClick={() => setStepsOpen(!stepsOpen)}
            aria-expanded={stepsOpen}
            className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-text-muted hover:text-primary-dark py-2 transition-colors"
          >
            Prefiero hacerlo a mano
            <ChevronDown
              size={16}
              className={`transition-transform ${stepsOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {stepsOpen && <InstallStepsCard guide={guide} className="mt-2" />}
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Por qué conviene (corto: el paso ya está dado arriba)             */}
      {/* ---------------------------------------------------------------- */}
      <ul className="space-y-2 mb-5">
        {PERKS.map((perk) => (
          <li key={perk.text} className="flex items-center gap-3 text-sm text-text-main">
            <span aria-hidden="true" className="text-lg leading-none">{perk.emoji}</span>
            <span className="leading-snug">{perk.text}</span>
          </li>
        ))}
      </ul>

      {/* ---------------------------------------------------------------- */}
      {/* Para el que ya la tiene                                           */}
      {/* ---------------------------------------------------------------- */}
      <div className="rounded-xl bg-success-bg border border-success/20 p-4 text-center">
        <p className="text-sm text-green-800 leading-snug">
          <strong>¿Ya tenés la app?</strong>
          <br />
          Cerrá esta pestaña y abrí <strong>Liga Ajupam</strong> desde tu pantalla de inicio.
        </p>
      </div>
    </div>
  );
};

export default InstallGuidePanel;
